// API wrapper for /admin/* (proxied to backoffice + market-maker by
// the api-gateway). Cookie auth + CSRF replay — identical contract to
// web-backoffice so a single shared admin session covers both apps.

const CSRF_KEY = "sm_admin_csrf";

export class APIError extends Error {
  status: number;
  code: string;
  body: unknown;
  constructor(status: number, code: string, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

export function getCSRF(): string | null {
  return typeof window !== "undefined" ? window.localStorage.getItem(CSRF_KEY) : null;
}

export function setCSRF(token: string | null) {
  if (typeof window === "undefined") return;
  if (token) window.localStorage.setItem(CSRF_KEY, token);
  else window.localStorage.removeItem(CSRF_KEY);
}

export interface ApiOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE" | "PUT";
  body?: unknown;
  signal?: AbortSignal;
  skipCSRF?: boolean;
  // Used by the login flow to surface the 428 NeedsMFA branch as a
  // returned object rather than throwing.
  expectMFAChallenge?: boolean;
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json" };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (!opts.skipCSRF && method !== "GET") {
    const tok = getCSRF();
    if (tok) headers["X-CSRF-Token"] = tok;
  }
  const res = await fetch(path, {
    method,
    headers,
    credentials: "include",
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });
  if (res.status === 204) return undefined as T;
  let json: unknown = null;
  const text = await res.text();
  if (text) {
    try { json = JSON.parse(text); } catch { json = text; }
  }
  if (res.status === 428 && opts.expectMFAChallenge) {
    return json as T;
  }
  if (!res.ok) {
    const err = (json as { error?: { code?: string; message?: string } })?.error;
    throw new APIError(
      res.status,
      err?.code ?? "UNKNOWN",
      err?.message ?? `HTTP ${res.status}`,
      json,
    );
  }
  return json as T;
}

// qs serializes a flat object into a leading-? query string. Keys
// with undefined / null / "" values are omitted so the URL stays
// tidy.
export function qs(params: Record<string, string | number | undefined | null>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === "" || v === null) continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}
